/**
 * Fachada da conversão de vídeo para canais Meta.
 *
 * Junta as três peças e é o único ponto que o `chatStore` precisa conhecer:
 *
 *   `limiteVideoMeta`  → qual é o teto deste canal
 *   `metadadosDeVideo` → duração e dimensões da origem
 *   `planoDeCompressao`→ precisa mexer? quanto?
 *   `ffmpegVideo`      → faz
 *
 * Regra de ouro: vídeo que **já cabe e já é mp4 sai intacto**. Converter à toa
 * degradaria a imagem e gastaria minutos do atendente.
 */

import { limiteDeVideo } from './limiteVideoMeta';
import { lerMetadadosDeVideo } from './metadadosDeVideo';
import { planejarCompressao } from './planoDeCompressao';
import { converterVideoViaWorker } from './ffmpegVideo';
import { resolverCaminhoDeVideo } from './videoPath';
import { converterViaWebCodecs, WebCodecsNaoSuportadoError } from './webcodecsVideo';

/**
 * Em que passo a conversão está.
 *
 * Só existem passos que de fato CONVERTEM: ler metadados e planejar leva
 * milissegundos e não merece aviso na tela. `recomprimindo` é a segunda
 * passada, quando o encoder do sistema estourou o alvo e o ffmpeg assume —
 * sem esse rótulo o progresso voltaria a zero sem explicação nenhuma.
 */
export type EtapaDeConversao = 'convertendo' | 'recomprimindo';

export interface OpcoesDeConversao {
    signal?: AbortSignal;
    onProgress?: (fraction: number) => void;
    onEtapa?: (etapa: EtapaDeConversao) => void;
}

export type ResultadoDeConversao =
    | { estado: 'intacto'; arquivo: File }
    | { estado: 'convertido'; arquivo: File; remuxado: boolean }
    | { estado: 'impossivel'; motivo: string };

/** Erro de conversão que já tem texto pronto para o operador. */
export class ConversaoDeVideoError extends Error {}

function trocarExtensaoParaMp4(nome: string): string {
    return nome.replace(/\.[^./\\]+$/, '') + '.mp4';
}

/**
 * Devolve o arquivo pronto para subir ao storage — o original, quando já serve,
 * ou um mp4 convertido.
 *
 * Não lança por vídeo grande demais: isso é `{ estado: 'impossivel' }`, com
 * motivo pronto para mostrar. Lança só quando a conversão em si falha.
 */
export async function converterVideoParaMeta(
    arquivo: File,
    plataforma: string | null | undefined,
    opts: OpcoesDeConversao = {},
): Promise<ResultadoDeConversao> {
    const limiteBytes = limiteDeVideo(plataforma);

    // Canal fora da Meta: outro transporte, outros limites. Não é nosso assunto.
    if (limiteBytes === null) return { estado: 'intacto', arquivo };

    let duracaoSec = 0;
    let larguraPx: number | null = null;
    let alturaPx: number | null = null;
    try {
        const meta = await lerMetadadosDeVideo(arquivo);
        duracaoSec = meta.duracaoSec;
        larguraPx = meta.larguraPx;
        alturaPx = meta.alturaPx;
    } catch {
        // Metadados ilegíveis não barram um arquivo que já cabe: só impedem
        // calcular compressão. O plano abaixo decide, com duracaoSec = 0.
    }

    const plano = planejarCompressao({
        tamanhoBytes: arquivo.size,
        duracaoSec,
        limiteBytes,
        larguraPx,
        mime: arquivo.type,
    });

    if (plano.acao === 'nao-precisa') return { estado: 'intacto', arquivo };
    if (plano.acao === 'impossivel') return { estado: 'impossivel', motivo: plano.motivo };

    const empacotar = (blob: Blob, remuxado: boolean): ResultadoDeConversao => ({
        estado: 'convertido',
        remuxado,
        arquivo: new File([blob], trocarExtensaoParaMp4(arquivo.name), {
            type: 'video/mp4',
            lastModified: Date.now(),
        }),
    });

    // Caminho rápido: codecs do sistema, normalmente por hardware. Num vídeo de
    // dezenas de MB a diferença para o ffmpeg.wasm single-thread é de minutos
    // para segundos.
    // Dimensões de SAÍDA, não de entrada: é o quadro que vai ser codificado que o
    // nível do H.264 precisa suportar. Consultar com as de entrada faria um 4K
    // destinado a 640px cair no wasm sem necessidade.
    const larguraSaida = plano.larguraMaxima ?? larguraPx;
    const alturaSaida =
        plano.larguraMaxima && larguraPx && alturaPx
            ? Math.round((alturaPx * plano.larguraMaxima) / larguraPx)
            : alturaPx;

    const decisao = await resolverCaminhoDeVideo(arquivo.type, larguraSaida, alturaSaida);
    let tentouWebCodecs = false;
    if (decisao.caminho === 'webcodecs' && decisao.codec) {
        tentouWebCodecs = true;
        opts.onEtapa?.('convertendo');
        try {
            const blob = await converterViaWebCodecs(
                {
                    blob: arquivo,
                    bitrateVideo: plano.bitrateVideo,
                    larguraMaxima: plano.larguraMaxima,
                    alvoBytes: plano.alvoBytes,
                    codec: decisao.codec,
                },
                opts,
            );
            // O encoder de hardware não aceita um teto rígido de arquivo: ele
            // mira o bitrate e erra para cima em material com muito movimento.
            // Estourou o alvo → o ffmpeg assume, que sabe fazer segunda passada.
            if (blob.size > 0 && blob.size <= plano.alvoBytes) return empacotar(blob, false);
        } catch (erro) {
            // Cancelamento é do usuário e deve subir; o resto é motivo para
            // tentar pelo caminho que funciona em todo lugar.
            if (erro instanceof DOMException && erro.name === 'AbortError') throw erro;
            if (!(erro instanceof WebCodecsNaoSuportadoError)) {
                console.warn('[video] WebCodecs falhou; caindo para ffmpeg.wasm:', erro);
            }
        }
    }

    opts.onEtapa?.(tentouWebCodecs ? 'recomprimindo' : 'convertendo');

    const { blob, remuxado } = await converterVideoViaWorker(
        {
            blob: arquivo,
            alvoBytes: plano.alvoBytes,
            bitrateVideo: plano.bitrateVideo,
            bitrateAudio: plano.bitrateAudio,
            larguraMaxima: plano.larguraMaxima,
            duracaoSec,
            // Só vale tentar o atalho sem perda quando o tamanho já cabe e o
            // problema era apenas o container.
            tentarRemux: arquivo.size <= limiteBytes,
        },
        opts,
    );

    return empacotar(blob, remuxado);
}
